"use client";

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';

import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';


const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const pathname = usePathname();
  const { user, loading } = useAuth();
  const [userRole, setUserRole] = useState<string | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchUserRole = async () => {
      if (user) {
        const { createClient } = await import('@/lib/supabase/client');
        const supabase = createClient();
        const { data } = await supabase?.from('user_profiles')?.select('role')?.eq('id', user?.id)?.single();
        setUserRole(data?.role || null);
      } else {
        setUserRole(null);
      }
    };
    fetchUserRole();
  }, [user]);

  const navLinks = [
  { id: 'nav_home', label: 'Home', href: user ? '/dashboard' : '/' },
  { id: 'nav_pricing', label: 'Why Free', href: '/pricing' },
  { id: 'nav_story', label: 'Our Story', href: '/ourstory' },
  { id: 'nav_faqs', label: 'FAQs', href: '/faq' },
  { id: 'nav_memorial', label: 'My Life Story', href: '/memorial' },
  { id: 'nav_pipa', label: 'Privacy & PIPA', href: '/pipa-compliance' },
  { id: 'nav_contact', label: 'Contact', href: '/faq' }];

  // Filter out My Memorial link if user is not logged in
  const filteredNavLinks = navLinks?.filter(link => 
    link?.id !== 'nav_memorial' || user
  );


  const isHomepage = pathname === '/homepage' || pathname === '/';
  const useTransparent = isHomepage && !isScrolled;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      useTransparent ? 'bg-transparent' : 'bg-slate-900/95 backdrop-blur-md shadow-md'}`
      }>

      <nav className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link href="/homepage" className="flex items-center gap-3 group">
            <span className={`font-jakarta font-bold text-xl tracking-tight ${useTransparent ? 'text-white' : 'text-white'}`}>
              Legacy.bm
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {filteredNavLinks?.map((link) =>
            <Link
              key={link?.id}
              href={link?.href}
              suppressHydrationWarning
              className={`text-sm font-medium transition-colors relative ${
              pathname === link?.href || link?.href?.includes('#') && (pathname === '/homepage' || pathname === '/') ?
              'text-primary' : useTransparent ? 'text-white/80 hover:text-white' : 'text-white/80 hover:text-white'}`
              }>

                {link?.label}
                {(pathname === link?.href || link?.href?.includes('#') && (pathname === '/homepage' || pathname === '/')) &&
              <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary rounded-full" />
              }
              </Link>
            )}
          </div>

          {/* Auth Buttons */}
          {!user && !loading &&
          <div className="hidden lg:flex items-center gap-4">
              <Link
              href="/login"
              className={`px-5 py-2.5 text-sm font-medium hover:text-primary transition-colors ${useTransparent ? 'text-white' : 'text-white'}`}>

                Log In
              </Link>
              <Link
              href="/signup"
              className="px-6 py-2.5 bg-primary rounded-full text-sm font-semibold hover:bg-primary/90 transition-all shadow-lg hover:shadow-xl text-[rgba(247,247,247,1)]">

                Start Your Legacy
              </Link>
            </div>
          }

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`lg:hidden p-2 hover:text-primary transition-colors ${useTransparent ? 'text-white' : 'text-white'}`}
            aria-label="Toggle menu">

            <Icon name={isMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={28} />
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen &&
        <div className="lg:hidden mt-6 pb-6 border-t border-border pt-6 animate-fade-in-up">
            <div className="flex flex-col gap-4">
              {filteredNavLinks?.map((link) =>
            <Link
              key={link?.id}
              href={link?.href}
              onClick={() => setIsMenuOpen(false)}
              className={`text-base font-medium py-2 transition-colors ${
              pathname === link?.href ?
              'text-primary' : 'text-muted-foreground hover:text-foreground'}`
              }>

                  {link?.label}
                </Link>
            )}
              <hr className="border-border my-2" />
              {!user && !loading &&
            <>
                  <Link
                href="/login"
                onClick={() => setIsMenuOpen(false)}
                className="w-full py-3 text-center font-medium text-foreground bg-muted rounded-xl hover:bg-muted/80 transition-colors">

                    Log In
                  </Link>
                  <Link
                href="/signup"
                onClick={() => setIsMenuOpen(false)}
                className="w-full py-3 text-center font-semibold text-primary-foreground bg-primary rounded-xl hover:bg-primary/90 transition-colors">

                    Start Your Legacy
                  </Link>
                </>
            }
            </div>
          </div>
        }
      </nav>
    </header>);

};

export default Header;